import React from 'react';

export type TypographyVariant = 
  | 'h1' 
  | 'h2' 
  | 'h3' 
  | 'h4' 
  | 'h5' 
  | 'h6' 
  | 'body1' 
  | 'body2' 
  | 'caption' 
  | 'overline'
  | 'subtitle1'
  | 'subtitle2';

export type TypographyAlign = 'left' | 'center' | 'right' | 'justify';

export type TypographyColor = 
  | 'primary' 
  | 'secondary' 
  | 'success' 
  | 'tertiary' 
  | 'danger' 
  | 'warning' 
  | 'info' 
  | 'text-primary' 
  | 'text-secondary' 
  | 'text-muted'
  | 'inherit';

export interface TypographyProps {
  /** The content to display */
  children: React.ReactNode;
  /** Typography variant that determines size and weight */
  variant?: TypographyVariant;
  /** HTML element to render as */
  component?: keyof JSX.IntrinsicElements;
  /** Text alignment */
  align?: TypographyAlign;
  /** Text color */
  color?: TypographyColor;
  /** Use retro/gaming display font (Chakra Petch) */
  retro?: boolean;
  /** Make text bold */
  bold?: boolean;
  /** Make text italic */
  italic?: boolean;
  /** Make text uppercase */
  uppercase?: boolean;
  /** Make text lowercase */
  lowercase?: boolean;
  /** Make text capitalize */
  capitalize?: boolean;
  /** Disable text wrapping */
  noWrap?: boolean;
  /** Truncate text with ellipsis */
  truncate?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  onClick?: any;
}

const defaultComponents: Record<TypographyVariant, keyof JSX.IntrinsicElements> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
};

export const Typography: React.FC<TypographyProps> = ({
  children,
  variant = 'body1',
  component,
  align = 'left',
  color = 'inherit',
  retro = false,
  bold = false,
  italic = false,
  uppercase = false,
  lowercase = false,
  capitalize = false,
  noWrap = false,
  truncate = false,
  className = '',
  style,
  onClick,
  ...props
}) => {
  const Component = component || defaultComponents[variant];
  
  const classes = [
    'prometheus-typography',
    `prometheus-typography--${variant}`,
    `prometheus-typography--align-${align}`,
    color !== 'inherit' && `prometheus-typography--color-${color}`,
    retro && 'prometheus-typography--retro',
    bold && 'prometheus-typography--bold',
    italic && 'prometheus-typography--italic',
    uppercase && 'prometheus-typography--uppercase',
    lowercase && 'prometheus-typography--lowercase',
    capitalize && 'prometheus-typography--capitalize',
    noWrap && 'prometheus-typography--no-wrap',
    truncate && 'prometheus-typography--truncate',
    className,
  ].filter(Boolean).join(' ');

  return (
    <Component 
      className={classes} 
      style={style}
      onClick={onClick}
      {...props}
    >
      {children}
    </Component>
  );
};

export default Typography;